import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { DeploymentDataItem } from './useDeploymentsData';
import { ActionHandlers } from '../components/DeployedPipelinesTable/cellRenderers';
import type { DeploymentSelect } from '../Types';

import i18n from '~/common/helpers/i18n';
import { buildPipelineVersionDiffViewerUrl } from '~/common/helpers/url/designVersionDiffPaths';
import ConfirmDialog from '~/components/ConfirmDialogV2';
import { Pipeline } from '~/entities/Pipeline';
import { StoreRootState } from '~/scenes/Monitor/types/StoreRootState';

type UseDeploymentMenuActionHandlersProps = {
  onShowModalDeployment: (deployment: DeploymentSelect) => void;
  goToPipeline: (pipelineId: string) => void;
  remove: ({
    environment,
    realm,
    deployment
  }: {
    environment: string;
    realm: string;
    deployment: string;
  }) => Promise<void>;
  redeploy: (pipeline: Pipeline) => void;
  openDetailPromote: (deployment: DeploymentSelect) => void;
  openModalRollbackDeploy: (deployment: DeploymentSelect) => void;
  onViewExecutions: (pipelineName: string) => void;
};

export const useDeploymentMenuActionHandlers = ({
  onShowModalDeployment,
  goToPipeline,
  remove,
  redeploy,
  openDetailPromote,
  openModalRollbackDeploy,
  onViewExecutions
}: UseDeploymentMenuActionHandlersProps): ActionHandlers => {
  const { environment, activeRealm } = useSelector(
    (state: StoreRootState) => state.application
  );

  const confirmDialog = ConfirmDialog.useConfirmDialog();

  const handleDeleteDeploy = useCallback(
    async (deployment: DeploymentDataItem['deployment']) => {
      if (remove) {
        confirmDialog.show({
          title: i18n.t('scenes.runtime.actions.confirm') as string,
          content: i18n.t('scenes.runtime.messages.prompt.undeploy') as string,
          danger: true,
          confirmText: i18n.t('scenes.runtime.actions.confirm') as string,
          cancelText: i18n.t('scenes.runtime.actions.cancel') as string,
          onConfirm: async () => {
            try {
              await remove({
                environment,
                realm: activeRealm,
                deployment: deployment.id
              });
            } catch (error) {
              // Error handling for deployment removal
            }
          },
          showEnvironmentTag: true
        });
      }
    },
    [remove, confirmDialog, environment, activeRealm]
  );

  const handleOpenVersionDiff = useCallback(
    (pipelineName: string, version: string) => {
      window.open(
        buildPipelineVersionDiffViewerUrl(activeRealm, pipelineName, version),
        '_blank'
      );
    },
    [activeRealm]
  );

  return {
    goToPipeline,
    onDeleteDeploy: handleDeleteDeploy,
    redeploy,
    openDetailPromote,
    openModalRollbackDeploy,
    onClick: onShowModalDeployment,
    onViewExecutions,
    onOpenVersionDiff: handleOpenVersionDiff,
    environment
  };
};
